import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { ReactComponent as ClockSVG } from '../../../../assets/images/home/core-benefits-clock.svg';
import { ReactComponent as ImageSVG } from '../../../../assets/images/home/core-benefits-image.svg';
import { ReactComponent as MessageSVG } from '../../../../assets/images/home/core-benefits-message.svg';
import { ReactComponent as SendSVG } from '../../../../assets/images/home/core-benefits-send.svg';
import { ReactComponent as TabletSVG } from '../../../../assets/images/home/core-benefits-tablet.svg';
import { ReactComponent as ThumbUpSVG } from '../../../../assets/images/home/core-benefits-thumb-up.svg';
import Footer from '../../components/Footer';
import Header from '../../components/Header/Header';
import PreFooterBlock from '../../components/PreFooterBlock';
import './styles.scss';

const coreBenefitsSteps = [
  {
    iconComponent: <MessageSVG />,
    title: 'Targeted Messaging to Staff',
  },
  {
    iconComponent: <SendSVG />,
    title: 'Clear Care Communication',
  },
  {
    iconComponent: <ThumbUpSVG />,
    title: 'Digital Acknowledgement',
  },
  {
    iconComponent: <ImageSVG />,
    title: 'Share Media with Families',
  },
  {
    iconComponent: <TabletSVG />,
    title: 'No More Paper Memos',
  },
  {
    iconComponent: <ClockSVG />,
    title: 'One source of Information - No Misunderstanding',
  },
];

const OperationsPage = () => {
  const coreBenefitsSwiperRef = useRef();

  const [isOpenScheduleDemoModal, setIsOpenScheduleDemoModal] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <main className={cx('home-page communication-page', { isMobile })}>
      <Header setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal} isOpenScheduleDemoModal={isOpenScheduleDemoModal} />
      <div className="home-page-content">
        <div className="streamline">
          <div className="streamline-left">
            <h2 className="streamline-title">
              Improve Communication <br />
              <span className="blue-part">Across Teams and Families</span>
            </h2>
            <p className="streamline-subtitle">Distribute updates, messages, and documents with clarity, speed, and control.</p>
            <button className="request-demo-btn" onClick={() => setIsOpenScheduleDemoModal(true)}>
              Request a Demo{' '}
              <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 9.89771H14.25" stroke="#448AFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path
                  d="M9 4.64771L14.25 9.89771L9 15.1477"
                  stroke="#448AFF"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="manual-process">
          <div className="manual-process-left-block">
            <h3 className="say-goodbye-title">One Communication Platform Across All Communities</h3>
          </div>
          <div className="manual-process-right-block">
            <ul className="manual-process-list">
              <li className="manual-process-list-item">Send documents for signatures via texts</li>
              <li className="manual-process-list-item">Keep staff communication clear and timely</li>
              <li className="manual-process-list-item">Instantly share updates with multiple families</li>
              <li className="manual-process-list-item">Notify team members and keep notification logs</li>
            </ul>
          </div>
        </div>

        <div className="compliance-core-benefits">
          <h3 className="compliance-core-benefits-title">Core Benefits</h3>
          <div className="compliance-core-benefits-slider">
            <Swiper
              modules={[Navigation, Pagination]}
              onSwiper={(swiper) => (coreBenefitsSwiperRef.current = swiper)}
              spaceBetween={20}
              slidesPerView={isMobile ? 1 : 1.5}
              loop
              pagination={{ clickable: true }}
              breakpoints={{
                768: { slidesPerView: 2.5 },
                1024: { slidesPerView: 3.2 },
              }}
            >
              {coreBenefitsSteps.map((step, index) => (
                <SwiperSlide key={index}>
                  <div className="compliance-core-benefits-slide">
                    <div style={{ height: '30px' }}></div>
                    <div className="icon-wrapper">{step.iconComponent}</div>
                    <div className="compliance-core-benefits-slide-content">
                      <p className="compliance-core-benefits-title">{step.title}</p>
                      <p className="compliance-core-benefits-subtitle">{step.subtitle}</p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="custom-nav">
              <button onClick={() => coreBenefitsSwiperRef.current?.slidePrev()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12.7954H5" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 19.7954L5 12.7954L12 5.79541"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button onClick={() => coreBenefitsSwiperRef.current?.slideNext()}>
                <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5 12.7954H19" stroke="#3571D6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path
                    d="M12 5.79541L19 12.7954L12 19.7954"
                    stroke="#3571D6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <PreFooterBlock
          title="Bring clarity and confidence to every message."
          subtitle="Use AllCareForms to simplify how your team communicates internally and externally."
          setIsOpenScheduleDemoModal={setIsOpenScheduleDemoModal}
        />
      </div>

      <Footer />
    </main>
  );
};

export default OperationsPage;
